import {
  Box,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  Popover,
  Tooltip,
} from '@mantine/core';
import { IconFileDownload } from '@tabler/icons-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ResultsSummary, ResultsValuesResponse } from '../../data-access/types/results';
import { ResultsFiltersMenu } from '../results-filters-menu/filters';
import { ResultsFiltersButton } from '../results-filters-menu/filters-button';
import { ResultsClauseHelp } from '../results-help/results-clause-help';
import { ResultsClause } from '../results-help/results-clause-help-content';

import { ResultsClauseCards } from './results-clause-cards';
import classes from './results-header.module.css';
import { ResultsQuickFilters } from './results-quick-filters';
import { ResultsSegmentedToggle } from './results-segmented-toggle';
import { ResultsStatsHeader } from './results-stats-header';

import { useResultsColFilter } from '../../hooks/use-results-col-filter';

export type ResultsView = 'table' | 'map';
export type { ResultsClause };

export interface ResultsHeaderProps {
  view: ResultsView;
  onViewChange: (view: ResultsView) => void;
  filterValues: ResultsValuesResponse | undefined;
  filterValuesLoading?: boolean;
  summary: ResultsSummary | undefined;
  statsLoading?: boolean;
  exportDisabled: boolean;
  exportLoading: boolean;
  onExport: () => void;
  exportDisabledForClause: boolean;
}

export const ResultsHeader: FC<ResultsHeaderProps> = ({
  view,
  onViewChange,
  filterValues,
  filterValuesLoading,
  summary,
  statsLoading,
  exportDisabled,
  exportLoading,
  onExport,
  exportDisabledForClause,
}) => {
  const { t } = useTranslation();
  const [clauseFilter, setClauseFilter] = useResultsColFilter<string>('clause');
  const clause: ResultsClause = clauseFilter === 'gp' ? 'gp' : 'new';

  const viewOptions = [
    { value: 'table' as const, label: t('sampling.results.nav.table') },
    { value: 'map' as const, label: t('sampling.results.nav.map') },
  ];

  return (
    <Paper p="sm" withBorder={false}>
      <div className={classes.header}>
        <div className={classes.topRow}>
          <h1 className={classes.pageTitle}>{t('common.results')}</h1>
          <ResultsClauseCards
            onChange={setClauseFilter}
            value={clause}
          />
          <ResultsStatsHeader isLoading={statsLoading} summary={summary} />
        </div>

        <div className={classes.controlsRow}>
          <Group gap="sm">
            <ResultsSegmentedToggle
              onChange={(next) => onViewChange(next as ResultsView)}
              options={viewOptions}
              value={view}
            />
            <Popover position="bottom-start" width={400}>
              <Popover.Target>
                <ResultsFiltersButton />
              </Popover.Target>
              <Popover.Dropdown>
                {filterValues ? (
                  <ResultsFiltersMenu values={filterValues} />
                ) : filterValuesLoading ? (
                  <Center p="md">
                    <Loader size="sm" type="dots" />
                  </Center>
                ) : null}
              </Popover.Dropdown>
            </Popover>
          </Group>

          <Group gap="sm" wrap="wrap">
            <ResultsQuickFilters />
            <Tooltip
              disabled={!exportDisabledForClause}
              label={t('sampling.results.export_disabled_gp')}
              multiline
              w={320}
              withArrow
            >
              <Box component="span" display="inline-block">
                <Button
                  disabled={exportDisabled}
                  leftSection={<IconFileDownload size={16} />}
                  loading={exportLoading}
                  onClick={onExport}
                  size="compact-sm"
                  variant="default"
                >
                  {t('common.export')}
                </Button>
              </Box>
            </Tooltip>
          </Group>
        </div>

        <ResultsClauseHelp clause={clause} />
      </div>
    </Paper>
  );
};

